import React from 'react';
import { Link } from 'react-router-dom';
import { projectUrl, ORDERED_PROJECTS } from '../../utils/projectsData';
import { playNextNote, HOVER_COLORS } from '../../utils/hoverMelody';

/* The index: the work as a table, filtered by discipline.
 *
 * The old site's view of the work, running on this site's data rather than on a
 * hand-typed table that had to be edited every time a project changed. The
 * markup is work/index.html's — nav.filters of a.btn.filter, table#work, rows
 * carrying data-disciplines — so the grid's own rules apply to it unchanged.
 *
 * Built into the page rather than embedded as a document of its own. That means
 * the grid's rules have to be scoped (see .index-view in Header.css, copied
 * declaration for declaration from css/index.css), but everything else on the
 * site reaches it: chaos mode distorts these rows because the engine already
 * looks for tr, th, td and .btn, and the hover keyboard plays off the links.
 *
 * The disciplines are collected from the work rather than listed, so a new one
 * appears as a filter the moment a project claims it, and one nobody uses any
 * more disappears. That list drifting is what the old table couldn't help.
 */

const ALL = 'all';

// as the old table wrote it in data-disciplines: lowercase, spaces to hyphens
const slug = (d) => d.trim().toLowerCase().replace(/\s+/g, '-');

const disciplinesOf = (p) =>
  (p.discipline || '').split(',').map((d) => d.trim()).filter(Boolean);

export default function IndexTable() {
  const [filter, setFilter] = React.useState(ALL);

  /* The same order as the tiles and the list. The work should be in the same
     place wherever you look at it. */
  const rows = ORDERED_PROJECTS;

  const disciplines = React.useMemo(() => {
    const seen = new Set();
    rows.forEach(({ p }) => disciplinesOf(p).forEach((d) => seen.add(d)));
    return [...seen].sort();
  }, [rows]);

  const shown = filter === ALL
    ? rows
    : rows.filter(({ p }) => disciplinesOf(p).map(slug).includes(filter));

  const pick = (key) => (e) => { e.preventDefault(); setFilter(key); };

  return (
    <div className="index-view">
      <nav className="filters">
        <a
          href="#"
          className={'btn filter' + (filter === ALL ? ' active' : '')}
          data-filter={ALL}
          onClick={pick(ALL)}
        >
          All
        </a>
        {disciplines.map((d) => (
          <a
            key={d}
            href="#"
            className={'btn filter' + (filter === slug(d) ? ' active' : '')}
            data-filter={slug(d)}
            onClick={pick(slug(d))}
          >
            {d}
          </a>
        ))}
      </nav>

      <table id="work">
        <tbody>
          <tr>
            <th className="width-min">Project</th>
            <th className="width-auto">Discipline</th>
          </tr>
          {shown.map(({ p, id }) => (
            <tr key={`project-${id}`} data-disciplines={disciplinesOf(p).map(slug).join(',')}>
              <td className="width-min">
                <Link
                  to={projectUrl(p)}
                  style={{ '--hover-color': HOVER_COLORS[id % HOVER_COLORS.length] }}
                  onMouseEnter={playNextNote}
                >
                  {p.title}
                </Link>
              </td>
              <td className="width-auto">{p.discipline}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
